import { useEffect, useState, useCallback } from "react";
import apiClient from "@/lib/apiClient";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Download, Lock, CornerDownRight, GitCommitHorizontal, Loader2, FileText } from "lucide-react";

const STATUS_LABELS = { new: "New", in_progress: "In progress", resolved: "Resolved", wont_fix: "Won't fix" };
const TYPE_WORD = { screenshot: "screenshot", screen_recording: "screen recording", voice: "voice note", text: "text note" };
const MEDIA_WORD = { screenshot: "image attached", screen_recording: "video attached", voice: "audio attached" };
const ACTIONS = [
  ["all", "All changes"], ["created", "Submitted"], ["status_changed", "Status change"],
  ["commented", "Internal note"], ["public_reply", "Public reply"], ["tester_reply", "Tester reply"],
  ["tag_added", "Tag added"], ["tag_removed", "Tag removed"], ["archived", "Archived"],
  ["restored", "Restored"], ["jira_linked", "Jira linked"], ["deleted", "Deleted"],
];

function describe(a) {
  switch (a.action) {
    case "created": {
      const t = TYPE_WORD[a.feedback_type] || "feedback";
      const note = (a.feedback_note || "").trim();
      const media = MEDIA_WORD[a.feedback_type];
      const showMedia = media && (a.feedback_has_media || a.feedback_type === "screen_recording" || a.feedback_type === "voice");
      let s = `Submitted a ${t}`;
      if (note) s += `: “${note}”`;
      if (showMedia) s += ` [${media}]`;
      return s;
    }
    case "status_changed": return `Status: ${STATUS_LABELS[a.from_val] || a.from_val || "—"} → ${STATUS_LABELS[a.to_val] || a.to_val}`;
    case "priority_changed": return `Priority → ${a.to_val}`;
    case "tag_added": return `Tag added “${a.to_val}”`;
    case "tag_removed": return `Tag removed “${a.from_val}”`;
    case "archived": return "Archived";
    case "restored": return "Restored";
    case "deleted": return "Deleted";
    case "jira_linked": return `Linked to Jira ${a.to_val}`;
    case "commented": return `Internal note: ${a.body}`;
    case "public_reply": return `Public reply: ${a.body}`;
    case "tester_reply": return `Tester reply: ${a.body}`;
    default: return a.action;
  }
}

function KindIcon({ kind }) {
  if (kind === "comment") return <Lock className="h-3.5 w-3.5 text-ink/60" />;
  if (kind === "reply") return <CornerDownRight className="h-3.5 w-3.5 text-teal" />;
  return <GitCommitHorizontal className="h-3.5 w-3.5 text-ink/40" />;
}

export const ActivityTab = ({ projectId }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [action, setAction] = useState("all");
  const [groupBySubmission, setGroupBySubmission] = useState(false);

  const params = useCallback(() => {
    const p = {};
    if (from) p.date_from = from;
    if (to) p.date_to = to;
    if (action && action !== "all") p.action = action;
    return p;
  }, [from, to, action]);

  const PAGE = 100;
  const load = useCallback(() => {
    setLoading(true);
    apiClient.get(`/projects/${projectId}/activity`, { params: { ...params(), skip: 0, limit: PAGE } })
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total || 0); setHasMore(!!data.has_more); })
      .catch(() => { setItems([]); setTotal(0); setHasMore(false); })
      .finally(() => setLoading(false));
  }, [projectId, params]);

  const loadMore = useCallback(() => {
    setLoadingMore(true);
    apiClient.get(`/projects/${projectId}/activity`, { params: { ...params(), skip: items.length, limit: PAGE } })
      .then(({ data }) => { setItems((prev) => [...prev, ...(data.items || [])]); setHasMore(!!data.has_more); })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [projectId, params, items.length]);

  useEffect(() => { load(); }, [load]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const exportParams = { ...params(), ...(groupBySubmission ? { group: "submission" } : {}) };
      const res = await apiClient.get(`/projects/${projectId}/activity/export`, { params: exportParams, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tugboat-activity-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error("Export failed. Try again.");
    } finally { setExporting(false); }
  };

  return (
    <div data-testid="activity-tab">
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className="label-overline block mb-1">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="activity-from"
            className="border-2 border-ink rounded-xl px-3 py-2 font-700 text-sm bg-white" />
        </div>
        <div>
          <label className="label-overline block mb-1">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="activity-to"
            className="border-2 border-ink rounded-xl px-3 py-2 font-700 text-sm bg-white" />
        </div>
        <div className="min-w-[180px]">
          <label className="label-overline block mb-1">Change type</label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="border-2 border-ink rounded-xl font-700 bg-white" data-testid="activity-action"><SelectValue /></SelectTrigger>
            <SelectContent className="border-2 border-ink rounded-2xl">
              {ACTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 ml-auto text-sm font-700 text-ink/80 cursor-pointer select-none" data-testid="activity-group-toggle">
          <input type="checkbox" checked={groupBySubmission} onChange={(e) => setGroupBySubmission(e.target.checked)}
            className="h-4 w-4 accent-orange border-2 border-ink rounded" />
          Group by submission
        </label>
        <button onClick={exportPdf} disabled={exporting} data-testid="activity-export-pdf"
          className="nb-btn bg-ink text-cream text-sm px-4 py-2.5 disabled:opacity-60">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4" /> Export PDF</>}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-ink/50 font-600 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading activity…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-ink/20 rounded-3xl">
          <FileText className="h-8 w-8 mx-auto text-ink/30 mb-2" />
          <p className="font-700 text-ink/60">No activity in this range.</p>
          <p className="text-sm text-ink/40 font-600 mt-1">Status changes, notes and replies will show here.</p>
        </div>
      ) : (
        <>
          <p className="text-sm font-700 text-ink/50 mb-3">{total} change{total === 1 ? "" : "s"}</p>
          <ul className="space-y-2">
            {items.map((a) => (
              <li key={a.id} data-testid={`activity-row-${a.id}`}
                className="flex items-start gap-3 bg-white border-2 border-ink rounded-2xl px-4 py-3">
                <span className="mt-0.5 shrink-0"><KindIcon kind={a.kind} /></span>
                <span className="shrink-0 font-mono font-800 text-xs bg-sand border-2 border-ink rounded-lg px-2 py-0.5 mt-0.5">
                  {a.feedback_number ? `#${a.feedback_number}` : "—"}
                </span>
                <div className="min-w-0 flex-1">
                  {a.feedback_summary && (
                    <p className="text-xs font-800 text-ink/70 truncate" data-testid={`activity-row-summary-${a.id}`} title={a.feedback_summary}>{a.feedback_summary}</p>
                  )}
                  <p className="text-sm font-700 break-words">{describe(a)}</p>
                  {a.action === "created" && a.feedback_page_url && (
                    <p className="text-xs text-ink/45 font-600 mt-0.5 break-all" data-testid={`activity-row-url-${a.id}`}>on {a.feedback_page_url}</p>
                  )}
                  <p className="text-xs text-ink/50 font-600 mt-0.5">
                    {a.actor_name}{a.actor_type === "tester" ? " (tester)" : ""} · {new Date(a.created_at).toLocaleString()}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          {hasMore && (
            <div className="flex justify-center mt-4">
              <button onClick={loadMore} disabled={loadingMore} data-testid="activity-load-more"
                className="nb-btn bg-white text-ink text-sm px-5 py-2.5 disabled:opacity-60">
                {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : `Load more (${total - items.length} left)`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
